import { ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  Building2,
  Inbox,
  ScrollText,
  Settings,
  LogOut,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePendingRequestsCount } from "@/hooks/usePendingRequestsCount";

interface AdminLayoutProps {
  title: string;
  children: ReactNode;
}

interface NavItem {
  to: string;
  label: string;
  icon: typeof LayoutDashboard;
  end?: boolean;
  showBadge?: boolean;
}

const navItems: NavItem[] = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/users", label: "Users", icon: Users },
  { to: "/companies", label: "Companies", icon: Building2 },
  { to: "/requests", label: "Requests", icon: Inbox, showBadge: true },
  { to: "/audit", label: "Audit Log", icon: ScrollText },
  { to: "/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout({ title, children }: AdminLayoutProps) {
  const { session, signOut } = useAuth();
  const navigate = useNavigate();
  const { data: pendingCount = 0 } = usePendingRequestsCount();

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const email = session?.user?.email ?? "";

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {/* Sidebar */}
      <aside className="fixed inset-y-0 left-0 w-60 bg-slate-900 border-r border-slate-800 flex flex-col">
        {/* Brand */}
        <div className="px-4 py-4 border-b border-slate-800">
          <div className="text-sm font-bold text-white leading-tight">Doyence</div>
          <div className="text-xs text-slate-400 leading-tight">Admin Portal</div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2">
          <ul className="space-y-0.5 px-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const badgeValue = item.showBadge ? pendingCount : 0;
              return (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      [
                        "group flex items-center gap-2.5 px-2.5 py-1.5 text-sm rounded-sm border-l-2 transition-colors",
                        isActive
                          ? "border-amber-500 bg-slate-800 text-white"
                          : "border-transparent text-slate-300 hover:bg-slate-800/60 hover:text-white",
                      ].join(" ")
                    }
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                    {item.showBadge && badgeValue > 0 && (
                      <span className="ml-auto text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-500 text-slate-950">
                        {badgeValue}
                      </span>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="px-3 py-3 border-t border-slate-800 space-y-2">
          <div className="text-xs text-slate-400 truncate" title={email}>
            {email}
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            className="w-full flex items-center justify-center gap-2 px-2 py-1.5 text-xs rounded-sm bg-slate-800 hover:bg-slate-700 text-slate-200 transition-colors"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main column */}
      <div className="pl-60 flex flex-col min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-10 h-14 bg-slate-800 border-b border-slate-700 flex items-center justify-between px-6">
          <h1 className="text-base font-semibold text-white">{title}</h1>
          <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded bg-slate-700 text-slate-300 border border-slate-600">
            Superuser
          </span>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-6">{children}</main>
      </div>
    </div>
  );
}
